import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tag, Plus, Pencil, Trash2, X, Palette, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const PRESET_COLORS = [
  { color: '#EF4444', text: '#FFFFFF', label: 'Red' },
  { color: '#F97316', text: '#FFFFFF', label: 'Orange' },
  { color: '#EAB308', text: '#1F2937', label: 'Yellow' },
  { color: '#22C55E', text: '#FFFFFF', label: 'Green' },
  { color: '#14B8A6', text: '#FFFFFF', label: 'Teal' },
  { color: '#3B82F6', text: '#FFFFFF', label: 'Blue' },
  { color: '#6366F1', text: '#FFFFFF', label: 'Indigo' },
  { color: '#8B5CF6', text: '#FFFFFF', label: 'Violet' },
  { color: '#EC4899', text: '#FFFFFF', label: 'Pink' },
  { color: '#F43F5E', text: '#FFFFFF', label: 'Rose' },
  { color: '#6B7280', text: '#FFFFFF', label: 'Gray' },
  { color: '#1F2937', text: '#FFFFFF', label: 'Dark' },
  { color: '#FDE68A', text: '#92400E', label: 'Amber' },
  { color: '#BBF7D0', text: '#166534', label: 'Mint' },
  { color: '#BFDBFE', text: '#1E40AF', label: 'Sky' },
  { color: '#E9D5FF', text: '#6B21A8', label: 'Lavender' },
];

const TagsSettings = ({ showToast }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [form, setForm] = useState({
    name: '',
    color: '#3B82F6',
    text_color: '#FFFFFF',
  });

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/tags`);
      setTags(res.data);
    } catch (err) {
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const resetForm = () => {
    setForm({ name: '', color: '#3B82F6', text_color: '#FFFFFF' });
    setEditing(null);
    setShowCustomColor(false);
  };

  const openEdit = (tag) => {
    setForm({ name: tag.name, color: tag.color, text_color: tag.text_color });
    setEditing(tag.id);
    setShowForm(true);
    setShowCustomColor(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast?.('Tag name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        text_color: form.text_color,
      };

      if (editing) {
        await axios.put(`${API}/settings/tags/${editing}`, payload);
        showToast?.('Tag updated', 'success');
      } else {
        await axios.post(`${API}/settings/tags`, payload);
        showToast?.('Tag created', 'success');
      }
      resetForm();
      setShowForm(false);
      fetchTags();
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Failed to save tag', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tag?')) return;
    try {
      await axios.delete(`${API}/settings/tags/${id}`);
      showToast?.('Tag deleted', 'success');
      fetchTags();
    } catch (err) {
      showToast?.('Failed to delete tag', 'error');
    }
  };

  const selectPreset = (preset) => {
    setForm({ ...form, color: preset.color, text_color: preset.text });
    setShowCustomColor(false);
  };

  const filteredTags = tags.filter(t =>
    !searchQuery || t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="tags-settings">
      <div className="px-6 py-4 bg-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Tag size={20} />
          Tags
        </h3>
        <button
          data-testid="add-tag-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          <Plus size={16} /> Add Tag
        </button>
      </div>

      <div className="p-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="tag-search"
            type="text"
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Form */}
        {showForm && (
          <div className="border border-gray-200 rounded-xl p-5 space-y-4 bg-gray-50" data-testid="tag-form">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">{editing ? 'Edit Tag' : 'New Tag'}</h4>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {/* Tag Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name</label>
              <input
                data-testid="tag-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Urgent, VIP, Follow Up"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
              <div className="flex items-center gap-3">
                <span
                  data-testid="tag-preview"
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  style={{ backgroundColor: form.color, color: form.text_color }}
                >
                  {form.name || 'Tag Name'}
                </span>
              </div>
            </div>

            {/* Color Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((preset) => (
                  <button
                    key={preset.label}
                    data-testid={`color-preset-${preset.label.toLowerCase()}`}
                    type="button"
                    onClick={() => selectPreset(preset)}
                    className="relative w-8 h-8 rounded-lg border-2 transition-all hover:scale-110"
                    style={{
                      backgroundColor: preset.color,
                      borderColor: form.color === preset.color ? '#1D4ED8' : 'transparent',
                    }}
                    title={preset.label}
                  >
                    {form.color === preset.color && (
                      <svg className="absolute inset-0 m-auto w-4 h-4" fill="none" viewBox="0 0 24 24" stroke={preset.text} strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
                <button
                  data-testid="color-custom-toggle"
                  type="button"
                  onClick={() => setShowCustomColor(!showCustomColor)}
                  className="w-8 h-8 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                  title="Custom color"
                >
                  <Palette size={14} className="text-gray-400" />
                </button>
              </div>

              {/* Custom Color Inputs */}
              {showCustomColor && (
                <div className="flex gap-3 mt-3" data-testid="custom-color-inputs">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Background</label>
                    <div className="flex items-center gap-2">
                      <input
                        data-testid="custom-bg-color"
                        type="color"
                        value={form.color}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                      />
                      <input
                        type="text"
                        value={form.color}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Text</label>
                    <div className="flex items-center gap-2">
                      <input
                        data-testid="custom-text-color"
                        type="color"
                        value={form.text_color}
                        onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                      />
                      <input
                        type="text"
                        value={form.text_color}
                        onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                data-testid="tag-cancel-btn"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                data-testid="tag-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Update Tag' : 'Create Tag'}
              </button>
            </div>
          </div>
        )}

        {/* Tags Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading tags...</div>
        ) : filteredTags.length === 0 ? (
          <div className="text-center py-12" data-testid="tags-empty-state">
            <Tag size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">{searchQuery ? 'No tags match your search' : 'No tags yet. Create one to get started.'}</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="tags-list">
            {filteredTags.map(tag => (
              <div
                key={tag.id}
                data-testid={`tag-item-${tag.id}`}
                className="group relative inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full text-sm font-medium transition-all hover:shadow-md"
                style={{ backgroundColor: tag.color, color: tag.text_color }}
              >
                <span>{tag.name}</span>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    data-testid={`tag-edit-${tag.id}`}
                    onClick={() => openEdit(tag)}
                    className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    data-testid={`tag-delete-${tag.id}`}
                    onClick={() => handleDelete(tag.id)}
                    className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagsSettings;
